<template lang="pug">
v-container(fluid)
    div.grid-layout
        div.grid-col-1
            v-card(outlined)
                v-card-title Categories
                v-card-subtitle
                    span requires 4 finalized (green check) categories

                v-list
                    v-list-item(
                        v-for="category in localCategories"
                        :key="category.id"
                        @click="selectCategory(category)"
                        :class="{ 'selected-category': selectedCategory && selectedCategory.id === category.id}"
                    )
                        v-list-item-content
                            v-list-item-title(class="small-font") {{ category.category_name }}
                                v-icon(:color="category.is_finalized ? 'green' : 'red'" class="ml-4")
                                     | {{ category.is_finalized ? 'check' : 'close' }}
        div.grid-col-2
            v-card(v-if="selectedCategory" outlined)
                v-card-title Edit {{ selectedCategory.category_name }}
                v-card-text
                    v-row
                        v-col
                            v-form(ref="categoryForm")
                                v-row
                                    v-col(cols="12" sm="8" md="5")
                                        v-select(
                                            label="UOM"
                                            v-model="selectedCategory.common_uom_id"
                                            :items="filteredUomItems"
                                            :item-text="uom => `${uom.uom_code} - ${uom.name}`"
                                            item-value="id"
                                            :rules="[rules.required, rules.validUom]"
                                            required
                                            color="brand"
                                        )
                                    v-col
                                        v-spacer
                    v-row(v-if="selectedCategory.is_finalized" dense)
                        v-col(cols="12" sm="8" md="6")
                            v-text-field(
                                v-model="selectedCategory.finalized_by"
                                label="Finalized By (read-only)",
                                readonly
                                color="brand"
                            )
                        v-col(cols="12" sm="6" md="4")
                            v-text-field(
                                :value="formattedFinalizedDate"
                                label="Finalized On (read-only)",
                                readonly
                                color="brand"
                            )
                    v-row
                        v-col(cols="12")
                           v-alert(
                               v-for="(msg, i) in categoryMessages"
                               :key="msg.id"
                               type="info"
                               dense outlined class="mb-2"
                           ) {{ msg.text }}
                    v-row(dense)
                        v-col(cols="12")
                            v-card(outlined)
                                v-card-title
                                    span Points
                                v-card-text
                                    template(v-if="selectedCategory.category_name === 'DEMAND'")
                                        v-btn(
                                            color="brand"
                                            class="white--text"
                                            @click="addNewPoint"
                                            :disabled="!selectedCategory"
                                        ) Add Calculated Point
                                    Points(
                                        :category="selectedCategory",
                                        :uom="localUom",
                                        :categories="localCategories"
                                        @update:points="handleUpdatePoints",
                                    )

                v-card-actions
                    template(v-if="selectedCategory.is_finalized")
                        v-btn(
                            color="orange"
                            @click="unFinalizeCategory"
                            class="ml-5"
                        ) UnFinalize Local Category
                    template(v-if="selectedCategory.canFinalize && !selectedCategory.is_finalized")
                        v-btn(
                            color="green"
                            @click="finalizeCategory"
                        ) Finalize Local Category
                    v-spacer
                    v-tooltip(top)
                        template(v-slot:activator="{ on }")
                            v-btn(
                                @click="saveCategory"
                                color="brand"
                                class="white--text mr-1"
                                :class="{ 'save-enabled-blink': isCategoryDirty && isValidCategory }"
                                :disabled=" !isValidCategory"
                                v-on="on"
                            ) Save Local Category
                        span Save Category Locally
                    v-btn(
                        color="gray"
                        @click="closeConfig"
                        class="mr-5"
                    ) Close

            v-card(v-else)
                v-card-title No Configuration Possible
                v-card-text Waiting for the Analytics Client to send points


</template>

<script>

// Only system save should write to the server
// all other changes should be local and then to the store state
// An over all save button will then run a store action to save to the server

import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import keys from 'lodash/keys';
import union from 'lodash/union';

import Points from './Points.vue';
import { nanoid, customAlphabet } from 'nanoid';
// max number for the point_id is 4,294,967,295
const nanoidNum = customAlphabet('1234567890', 9);

export default {
    name: 'Categories',
    emits: ['update:categories', 'close:categories'],
    components: {
        Points
    },
    props: {
        categories: {
            type: Array,
            required: true
        },
        systemName: {
            type: String,
            required: true
        },
        systemId: {
            type: String,
            required: true
        },
        uom: {
            type: Array,
            required: true
        }
    },
    data () {
        return {
            selectedCategory: {
                canFinalize: false,
                is_finalized: false,
            },
            originalCategory: null,
            selectedPredictionCategory: null,
            // openPoints: false,
            localCategories: cloneDeep(this.categories).map(cat => {
                cat.valid = false;
                return cat;
            }),
            localUom: [...this.uom],
            newItem: null,
            rules: {
                required: (value) => !!value || 'Required.',
                validUom: (value) => this.isValidCategoryUom(value),
            },
            defaultPoint: {
                container_dimensions: {},
                container_type: 'NONE',
                finalized_by: null,
                finalized_date: null,
                is_finalized: false,
                point_id: null,
                point_name: '',
                source_uom: '',
                standard_uom_id: null,
                point_type: 'CALC',
            },
            categoryMessages: [], // Array of messages to display in alerts
        };
    },
    methods: {
        selectCategory (category) {
            this.$set(this, 'selectedCategory', cloneDeep(category));
            this.$set(this, 'originalCategory', cloneDeep(category));
            this.updateFlagsInSelectedCategory();
        },

        closeConfig () {
            this.$emit('close:categories');
        },

        /**
         * Save the selected category to the local state and emit the updated categories
         * requires that the selectedCategory is set and that the common_uom_id is valid
         */
        saveCategory () {
            if(this.selectedCategory.common_uom_id <= 1) {
                this.setCategoryMessage('Please select a valid Unit of Measure (UOM)');
                return;
            }

            this.checkFinalizedCategoryModification();

            // if coming from a points update, propagate the points to the localCategories
            const index = this.localCategories.findIndex(cat => cat.id === this.selectedCategory.id);

            this.$set(this.localCategories, index, cloneDeep(this.selectedCategory));

            // update the store, via parent component
            this.$emit('update:categories', cloneDeep(this.localCategories));
            this.$set(this, 'originalCategory', cloneDeep(this.selectedCategory));

            this.setCategoryMessage('Category saved successfully');

            this.updateFlagsInSelectedCategory();
        },

        /**
         * Checks if finalized category has been modified and unfinalizes if needed
         */
        checkFinalizedCategoryModification () {
            if(this.selectedCategory.is_finalized && this.isCategoryDirty) {
                // find matching category in localCategories
                const index = this.localCategories.findIndex(cat => cat.id === this.selectedCategory.id);
                if (index !== -1) {
                    const originalCategory = this.localCategories[index];
                    if(originalCategory.is_finalized) {
                        // compare the original category with the selected category
                        // and log the differences
                        const changes = this.getDifferences(originalCategory, this.selectedCategory, 'id', 'valid', 'canFinalize', 'points');
                        if (Object.keys(changes).length > 0) {
                            if(changes['common_uom_id']) {
                                this.unFinalizeCategory();
                                this.setCategoryMessage('Category unfinalized due to UOM change');
                            }
                        }
                    }
                }
            }
        },

        /**
         * updated the canFinalize and valid flags in the selected category
         *   - valid: means the category has a valid UOM
         *   - canFinalize means the category is not finalized, has a valid UOM and at least one point is finalized
         */
        updateFlagsInSelectedCategory () {
            const somePointsFinalized = this.selectedCategory.points.some(point => point.is_finalized) || false;
            const valid = this.selectedCategory.common_uom_id > 1;
            const isFinalized = Boolean( this.selectedCategory.is_finalized );
            const canFinalize = !isFinalized && valid && somePointsFinalized;

            this.$set(this.selectedCategory, 'canFinalize', canFinalize);
            this.$set(this.selectedCategory, 'valid', valid);
        },

        /**
         *
         * @param {object} payload
         * @param {object} payload.category_id
         * @param {{}[]} payload.points
         */
        handleUpdatePoints (payload) {

            // this can only happen when we have a selectedCategory
            if (!this.selectedCategory) return;

            // if category is finalized, need to check if point changes require unfinalization
            if(this.selectedCategory.is_finalized ) {
                this.checkFinalizedPointsModification(payload);
            }

            console.log('Updating points for category:', this.selectedCategory.category_name, payload.points);
            this.$set(this.selectedCategory, 'points', payload.points);

            const index = this.localCategories.findIndex(cat => cat.id === this.selectedCategory.id);

            this.$set(this.localCategories[index], 'points', cloneDeep(payload.points));

            this.setCategoryMessage('Points updated successfully');

            // update the store, via parent component
            this.updateFlagsInSelectedCategory();
        },

        /**
         * Checks if finalized points have been modified and unfinalizes the category if needed
         * @param {object} payload
         */
        checkFinalizedPointsModification (payload) {
            // since this method mutates the selectedCategory, we need to check if it is finalized
            if(this.selectedCategory.is_finalized){
                for (const finalizedPoint of this.selectedCategory.points.filter(p => p.is_finalized)) {
                    const matchingPoint = payload.points.find(p => p.point_id === finalizedPoint.point_id && p.point_type === finalizedPoint.point_type);
                    if (!matchingPoint || (matchingPoint && this.getDifferences(finalizedPoint, matchingPoint, 'point_id')['is_finalized'])) {
                        this.unFinalizeCategory();
                        this.setCategoryMessage('Category unfinalized due to point modification');
                        break;
                    }
                }
            }

            // now check if we added any finalized points
            // since this method mutates the selectedCategory, we need to check if it is finalized
            if(this.selectedCategory.is_finalized){
                for (const finalizedPoint of payload.points.filter(p => p.is_finalized)) {
                    const matchingPoint = this.selectedCategory.points.find(p => p.point_id === finalizedPoint.point_id && p.point_type === finalizedPoint.point_type);
                    const changes = matchingPoint ? this.getDifferences(matchingPoint, finalizedPoint, 'point_id') : {};
                    if (!matchingPoint || changes['is_finalized']) {
                        this.unFinalizeCategory();
                        this.setCategoryMessage('Category unfinalized due to point modification');
                        break;
                    }
                }
            }
        },

        /**
         * Finalize the selected category, setting the finalized flag and date
         */
        finalizeCategory () {
            this.$set(this.selectedCategory, 'is_finalized', true);
            this.$set(this.selectedCategory, 'finalized_by', this.$store.state?.fullUser?.email || 'Unknown');
            this.$set(this.selectedCategory, 'finalized_date', new Date().toISOString());
            this.updateFlagsInSelectedCategory();
        },

        /**
         * Un-finalize the selected category, removing the finalized flag and date
         */
        unFinalizeCategory () {
            if(this.selectedCategory.is_finalized) {
                this.$set(this.selectedCategory, 'is_finalized', false);
                this.$set(this.selectedCategory, 'finalized_by', null);
                this.$set(this.selectedCategory, 'finalized_date', null);
                this.updateFlagsInSelectedCategory();
            }
        },

        /**
         *
         * @param {number} uom_id
         */
        isValidCategoryUom (uom_id) {
            const units = this.uom.filter(unit => unit.type === 'RATE' || unit.type === 'VOLUME');
            const isValid = units.some(unit => unit.id === uom_id);
            if (!isValid) {
                return `Unit of measure must be of type RATE or VOLUME`;
            }
            return true;
        },

        /**
         * Create a new calculated point with default values
         * and add it to the selected category's points.
         */
        addNewPoint () {
            if (!this.selectedCategory) return;

            const prodCategory = this.localCategories.find(cat => cat.category_name === 'PRODUCTION');
            const standard_uom_id = prodCategory?.common_uom_id || 1;
            const source_uom = this.localUom.find(uom => uom.id === standard_uom_id)?.uom_code || 'NONE';

            const newPoint = Object.assign({}, this.defaultPoint, {
                category_id: this.selectedCategory.id,
                point_id: parseInt( nanoidNum(), 10),
                point_name: `New Point-${nanoid(4)}`,
                standard_uom_id,
                source_uom,
                point_type: 'CALC',
                calculation_point_ids: {storage: [], production: []},
                container_type: 'NONE',
                storage: [],
                production: [],
            });

            const updatedPoints = cloneDeep(this.selectedCategory.points || []);
            updatedPoints.push(newPoint);

            this.handleUpdatePoints({ category_id: this.selectedCategory.id, points: updatedPoints });
        },

        compareObjects (objA, objB, ...omitKeys) {
            return isEqual(
                omit(objA, omitKeys),
                omit(objB, omitKeys)
            );
        },

        getDifferences (objA, objB, ...omitKeys) {
            // Get all keys from both objects except omitted ones
            const comparisonKeys = union(
                keys(objA),
                keys(objB)
            ).filter(key => !omitKeys.includes(key));

            const differences = {};

            const booleanLikeKeys = new Set(['is_prediction_category', 'is_finalized']);

            comparisonKeys.forEach(key => {
                let valueA = objA[key];
                let valueB = objB[key];

                // Normalize boolean-like fields
                if (booleanLikeKeys.has(key)) {
                    valueA = valueA === true || valueA === 1;
                    valueB = valueB === true || valueB === 1;
                }

                if (!isEqual(valueA, valueB)) {
                    differences[key] = {
                        oldValue: objA[key], // Keep original values for reference
                        newValue: objB[key]
                    };
                }
            });

            return differences;
        },

        setCategoryMessage (message, timeout = 4) {
            // Add new message with a unique id
            const msgObj = { id: nanoid(8), text: message };
            this.categoryMessages.push(msgObj);
            if (!this._categoryMessageTimeouts) {
                this._categoryMessageTimeouts = {};
            }
            const timeoutId = setTimeout(() => {
                const idx = this.categoryMessages.findIndex(m => m.id === msgObj.id);
                if (idx !== -1) {
                    this.categoryMessages.splice(idx, 1);
                }
                delete this._categoryMessageTimeouts[msgObj.id];
            }, timeout * 1000);
            this._categoryMessageTimeouts[msgObj.id] = timeoutId;
            // Limit to 2 messages
            if (this.categoryMessages.length > 2) {
                const oldest = this.categoryMessages[0];
                clearTimeout(this._categoryMessageTimeouts[oldest.id]);
                this.categoryMessages.shift();
                delete this._categoryMessageTimeouts[oldest.id];
            }
        },
    },
    computed: {
        formattedFinalizedDate () {
            return this.selectedCategory.finalized_date
                ? new Date(this.selectedCategory.finalized_date).toLocaleString()
                : '';
        },

        filteredUomItems () {
            return this.localUom.filter(uom => {
                if (['STORAGE', 'CONSUMPTION'].includes(this.selectedCategory.category_name)) {
                    return uom.type === 'VOLUME' || uom.type === 'NONE';
                }
                return uom.type === 'RATE' || uom.type === 'NONE';
            });
        },

        isValidCategory () {
            if (!this.selectedCategory) return false;

            // Check if the category has a valid UOM and at least one point
            const hasValidUom = this.selectedCategory.common_uom_id > 1;
            const hasPoints = this.selectedCategory.points && this.selectedCategory.points.length > 0;

            // Check if all points have a valid UOM. only finalized points are to be considered
            const finalizedPointsHaveValidUOM = this.selectedCategory.points
                .filter(point => point.is_finalized)
                .every(point => {
                    return point.source_uom && point.standard_uom_id;
                });

            // hasValidUom must be  true
            // if it has finalized points, then they must all have a valid UOM
            // if it has no finalized points, then it can be valid

            return hasValidUom && (hasPoints ? finalizedPointsHaveValidUOM : true);
        },

        isCategoryDirty () {
            console.log('Checking if category is dirty');
            if (!this.selectedCategory || !this.originalCategory) {
                console.warn('No selected or original category to compare');
                return false;
            }

            const differences = this.getDifferences(this.selectedCategory, this.originalCategory, 'id', 'valid', 'canFinalize');

            const isSame = this.compareObjects(this.selectedCategory, this.originalCategory, 'id', 'valid', 'canFinalize');

            console.log('isCategoryDirty:', !isSame);
            return !isSame;
        },
    },
    watch: {
        // watches the prop: categories
        categories: {
            handler (newVal) {
                if(newVal){
                    this.localCategories = cloneDeep(this.categories).map(cat => {
                        cat.valid = false;
                        return cat;
                    });
                    if (this.localCategories.length > 0) {
                        this.selectCategory(this.localCategories[0]);
                        this.$set(this, 'originalCategory', cloneDeep(this.localCategories[0]));
                    }
                }
            },
            immediate: true,
            deep: true
        },
        // watches the prop: uom
        uom (newVal) {
            this.localUom = [...newVal];
        },
    },
};
</script>

<style scoped>
.grid-layout {
  display: grid;
  grid-template-columns: 17% 83%;
  gap: 12px;
}
.grid-col-1 {
  min-width: 0;
}
.grid-col-2 {
  min-width: 0;
}
.selected-category {
  background-color: #e3f2fd !important;
}
.small-font {
    font-size: 14px;
}
.save-enabled-blink {
  animation: blink-enabled 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  box-shadow: 0 0 12px 2px #43a047;
  transform: scale(1.04);
}
@keyframes blink-enabled {
  0% {
    box-shadow: 0 0 0px #43a047, 0 0 0px #fff;
    transform: scale(1.0);
  }
  20% {
    box-shadow: 0 0 12px 2px #43a047, 0 0 4px #fff;
    transform: scale(1.04);
  }
  50% {
    box-shadow: 0 0 24px 6px #43a047, 0 0 8px #fff;
    transform: scale(1.08);
  }
  80% {
    box-shadow: 0 0 12px 2px #43a047, 0 0 4px #fff;
    transform: scale(1.04);
  }
  100% {
    box-shadow: 0 0 0px #43a047, 0 0 0px #fff;
    transform: scale(1.0);
  }
}
</style>
